import { useContext, ComponentType, useMemo } from 'react';

import TranslationContext from './TranslationContext';
import { TranslateOptions, Translate, GetTranslation } from './types';

const useTranslate = () => useContext(TranslationContext);

export const useLocale = () => ({
  locale: useTranslate().locale,
  updateLocale: useTranslate().setLocale,
});

export const useT = (base: string): Translate => {
  const translate = useTranslate();

  return useMemo(() => translate.rebase(base), [translate, base]);
};

export const t: GetTranslation = (key: string, options?: TranslateOptions) =>
  // eslint-disable-next-line react-hooks/rules-of-hooks
  useTranslate()(key, options);

export const withT = <
  P extends { [key: string]: any } = {},
  C extends Record<string, string> = {},
>(
  Component: ComponentType<P>,
  config?: C,
) =>
  function (props: Omit<P, keyof C>) {
    const translate = useTranslate();
    const tProps = useMemo(
      () =>
        config
          ? Object.entries(config).reduce(
              (result, [prop, base]) => ({
                ...result,
                [prop]: translate.rebase(base),
              }),
              {},
            )
          : { t: translate },
      [config, translate],
    );
    const mergedProps = { ...props, ...tProps } as P;

    return <Component {...mergedProps} />;
  };
